import React, { useEffect, useState } from "react";
import { List, Typography } from 'antd';

import {
    Space,
    Col,
    Show,
    Table, 
    Form, 
    Row, 
    Input, 
    DateField, 
    Button, 
    useTable, 
    Card, 
    Tag, 
    ShowButton, 
    FormProps, 
    Icons, 
    Checkbox,
} from "@pankod/refine-antd";
import { CrudFilters, useOne } from "@pankod/refine-core";

import { RecalculateBalancesOfVirtualAccountsModel } from "./components/model/recalculate-va-balance.model";
import { CreateNewRepaymentVaButtonModel } from "./components/model/create-new-repayment-va.model";
import { getQueryParam, setQueryParam } from "../../../utility/queryParams";
import { ICompany, VaFundingBank } from "../../../interfaces";
import { BankModel } from "../../company/components/models/bank.model";
import { CloseVaFundingAccountButtonModel } from "../../company/components/models/close-va-funding-account.model";
import { CreateDecentroAccountModel } from "./components/model/create-decentro-account.model";
import { CreateEaseBuzzWireVaModel } from "./components/model/create-eb-wire-va.model";
import BackTitle from "components/back-title";

const { Title, Text } = Typography;

export const VirtualAccountListing: React.FC<any> = () => {
    let [companyId, setCompanyId] = useState<string>(getQueryParam("companyId") ?? null);
    let [excludeNonActiveBankAccounts, setExcludeNonActiveBankAccounts] = useState<boolean>(false);
    let [tableRefetch, setTableRefetch] = useState(false);

    let { data: companyData, isLoading: companyLoading, refetch: refetchCompanyData } = useOne<ICompany>({
        id: '',
        dataProviderName: "grapghQl",
        resource: 'getCompaniesForManagementPortal',
        metaData: {
            filter: {
                id: companyId
            },
            tableRefetch: tableRefetch,
        },
        queryOptions: {
            enabled: !!companyId,
        },
    });
    const companyDetail: ICompany | undefined = companyData?.data;

    let { tableProps, searchFormProps } = useTable({
        dataProviderName: "grapghQl",
        resource: 'fundingBankAccountForCompany',
        syncWithLocation: true,
        metaData: {
            includeDeleted: true,
            companyId,
            tableRefetch: tableRefetch,
        },
        initialSorter: [
            {
                field: "createdAt",
                order: "desc",
            },
        ],
    });

    let {
        tableProps: virtualAccountForCompanyTableProps,
        searchFormProps: virtualAccountSearchFormProps,
        isLoading: isTableLoading,
        refetch: refetchTableProps
    }: any = useTable({
        dataProviderName: "grapghQl",
        resource: 'allVirtualAccountsForCompanyViaInternalPortalPaginated',
        syncWithLocation: true,
        metaData: {
            companyId,
            excludeNonActiveBankAccounts,
            tableRefetch: tableRefetch,
        },
        onSearch: (params: any) => {
            const filters: CrudFilters = [];
            const { tableRefetch, companyId, excludeNonActiveBankAccounts } = params;
            if (companyId) {
                setCompanyId(companyId);
            }
            if (excludeNonActiveBankAccounts) {
                setExcludeNonActiveBankAccounts(excludeNonActiveBankAccounts)
            }
            setTableRefetch(tableRefetch)
            return filters;
        },
    });

    useEffect(() => {
        const companyId = getQueryParam("companyId");
        if (companyId) {
            virtualAccountSearchFormProps?.form?.setFieldsValue({ companyId });
            setCompanyId(companyId)
        }
    }, []);

    useEffect(() => {
        setTableRefetch(false);
        refetchCompanyData();
    }, [tableRefetch]);

    useEffect(() => {
        if(companyId) {
            setQueryParam('companyId', companyId)
            virtualAccountSearchFormProps?.form?.setFieldsValue({ companyId });
        }
    }, [companyId]);

    const refetchFundingBankAccountList = (message: boolean) => {
        if (searchFormProps.form?.getFieldValue('tableRefetch')) {
            searchFormProps.form?.setFieldsValue({ tableRefetch: null });
        } else {
            searchFormProps.form?.setFieldsValue({ tableRefetch: message });
        }
        searchFormProps.form?.submit();
        setTableRefetch(message);
    };

    const refetchCompanyDetail = (message: boolean) => {
        setTableRefetch(message);
    };

    let totalPage
    if (virtualAccountForCompanyTableProps?.pagination) {
        let totalItem: number = virtualAccountForCompanyTableProps?.pagination.total || 0
        let pageSize: number = virtualAccountForCompanyTableProps?.pagination.pageSize || 0
        totalPage = Math.ceil(totalItem / pageSize)
    }

    return (
        <Show
            isLoading={companyLoading}
            title={<BackTitle title="Kodo Virtual Accounts" />}
            headerButtons={({ defaultButtons }) => (
                <List >
                    <List.Item>
                        <Space align="center">
                            {companyDetail && <CreateNewRepaymentVaButtonModel refetchCompanyDetail={refetchCompanyDetail} companyId={companyDetail.id} />}
                        </Space>
                        <Space>
                            {companyDetail && <RecalculateBalancesOfVirtualAccountsModel refetchCompanyDetail={refetchCompanyDetail} companyId={companyDetail.id} />}
                        </Space>
                    </List.Item>
                </List>
            )}
        >
            {companyDetail &&
                <>
                    <Col>
                        <Col lg={24} xs={24}>
                            <Card title="Filters">
                                <Filter formProps={virtualAccountSearchFormProps} />
                            </Card>
                        </Col>
                        <Show
                            isLoading={isTableLoading}
                            title={"Virtual Account Listing"}
                            headerButtons={({ defaultButtons }) => (
                                <List>
                                    <List.Item>
                                        <Space align="center">
                                            {companyId && <CreateDecentroAccountModel setTableRefetch={setTableRefetch} companyId={companyId} />}
                                        </Space>
                                        <Space>
                                            {companyId && <CreateEaseBuzzWireVaModel setTableRefetch={setTableRefetch} companyId={companyId} />}
                                        </Space>
                                    </List.Item>
                                </List>
                            )}
                        >
                            <Table {...virtualAccountForCompanyTableProps } rowKey="id" style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }} lg={24} md={24}>
                                <Table.Column
                                    title="Integration"
                                    dataIndex="bankAccount"
                                    render={(_, record: any) => {
                                        const integratorName = record?.serviceProviderIntegration?.serviceProviderIntegrator?.name;
                                        return (
                                            <Space>
                                                <Text> {integratorName || '-'}</Text>
                                            </Space>
                                        );
                                    }}
                                />
                                <Table.Column title="Account Number" dataIndex="accountNo" />
                                <Table.Column title="IFSC" dataIndex="ifsc" />
                                <Table.Column title="Balance" dataIndex="balance" />
                                <Table.Column
                                    title="Status"
                                    dataIndex="isActive"
                                    render={(_, record: any) => {
                                        const status = record.isActive;
                                        let value = 'Active';
                                        let color;
                                        switch (status) {
                                            case true:
                                                color = "green";
                                                break;
                                            case false:
                                                value = 'Disabled';
                                                color = "blue";
                                                break;
                                            default:
                                                return;
                                        }
                                        return <Tag color={color}>{value}</Tag>;
                                    }}
                                />
                                <Table.Column
                                    title="Added by"
                                    dataIndex="activatedBy"
                                    render={(value: any) => (
                                        value.displayName
                                    )} />
                                <Table.Column
                                    title="Added on"
                                    dataIndex="activatedAt"
                                    render={(value: any) => (
                                        <Space>

                                            {
                                                <DateField className="ml-2" value={value} format="LL" />
                                            }
                                        </Space>
                                    )}
                                />
                                <Table.Column
                                    title="Actions"
                                    dataIndex="actions"
                                    render={(_, element: any) => (
                                        <Space>
                                            <ShowButton
                                                size="small"
                                                recordItemId={element.id}
                                            />
                                        </Space>
                                    )}
                                />
                            </Table>
                            {
                                (virtualAccountForCompanyTableProps?.dataSource && virtualAccountForCompanyTableProps?.pagination) ?
                                    <span> Showing {virtualAccountForCompanyTableProps?.pagination?.current} - {totalPage} of <strong>{virtualAccountForCompanyTableProps?.pagination.total}</strong> Records</span> : ''
                            }
                        </Show>
                    </Col>
                    <Col style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }} lg={24} md={24}>
                        <List >
                            <List.Item>
                                <Title level={5}>Authorized Accounts (Kodo VA Funding)</Title>
                            </List.Item>
                        </List>
                        <Form layout="vertical" {...searchFormProps}>
                            <Row gutter={[16, 16]} hidden={true}>
                                <Col lg={6} xs={6}>
                                    <Form.Item hidden={true} label="Table Refetch field" name="tableRefetch">
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>
                        <Table {...tableProps} pagination={false} rowKey="id">
                            <Table.Column
                                title="Integration"
                                dataIndex="bankAccount"
                                render={(_, record: VaFundingBank) => {
                                    const integratorName = record?.bankAccount?.serviceProviderIntegration?.serviceProviderIntegrator?.name;
                                    return (
                                        <Space>
                                            <Text> {integratorName || '-'}</Text>
                                        </Space>
                                    );
                                }}
                            />
                            <Table.Column title="Account Name" dataIndex="accountName" />
                            <Table.Column title="Account Number" dataIndex="accountNumber" />
                            <Table.Column title="IFSC" dataIndex="ifsc" />
                            <Table.Column title="Added by" dataIndex="addedBy" />
                            <Table.Column title="" dataIndex="addedOn" />
                            <Table.Column
                                title="Added on"
                                dataIndex="addedOn"
                                render={(value) => (
                                    <Space>

                                        {
                                            <DateField className="ml-2" value={value} format="LL" />
                                        }
                                    </Space>
                                )}
                            />
                            <Table.Column
                                title="Actions"
                                dataIndex="actions"
                                render={(_, element: VaFundingBank) => (
                                    <Space>
                                        {
                                            !element.isDeleted && <CloseVaFundingAccountButtonModel
                                                refetchCompanyDetail={refetchFundingBankAccountList}
                                                fundingBankAccountValidationRequestId={element.lastValidationRequestId}
                                                companyId={companyId} bankAccountId={element.bankAccount?.id} />
                                        }
                                        {
                                            element.isDeleted && <Button disabled> Removed on <DateField className="ml-2" value={element.deletedOn} format="LL" /></Button>
                                        }
                                    </Space>
                                )}
                            />
                        </Table>
                    </Col>
                    <Col style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }} lg={24} md={24}>
                        <List >
                            <List.Item>
                                <Title level={5}> Bank Details</Title>
                                <BankModel refetchCompanyDetail={refetchCompanyData} companyDetail={companyDetail} />
                            </List.Item>
                        </List>
                        <Table dataSource={companyDetail?.bank} rowKey="id">
                            <Table.Column title="Account Name" dataIndex="accountName" />
                            <Table.Column title="Account Number" dataIndex="accountNumber" />
                            <Table.Column title="Mobile No" dataIndex="mobileNo" />
                            <Table.Column title="IFSC" dataIndex="ifsc" />
                            <Table.Column title="Bank Name" dataIndex="bankName" />
                        </Table>
                    </Col>
                </>
            }
        </Show>
    );
};

const Filter: React.FC<{ formProps: FormProps }> = ({ formProps }) => {
    return (
        <Form layout="vertical" {...formProps}>
            <Row gutter={[16, 16]}>
                <Col lg={8} xs={12}>
                    <Form.Item hidden={true} label="Table Refetch field" name="tableRefetch">
                        <Input />
                    </Form.Item>
                    <Form.Item label="Company Id" name="companyId">
                        <Input
                            placeholder="Company Id"
                            prefix={<Icons.SearchOutlined />}
                        />
                    </Form.Item>
                </Col>
                <Col lg={8} xs={24}>
                    <Form.Item label="Include Disabled" name="excludeNonActiveBankAccounts">
                        <Checkbox
                            onChange={
                                (e) => {
                                    formProps?.form?.setFieldsValue({excludeNonActiveBankAccounts: e.target.checked});
                                }
                            }
                        />
                    </Form.Item>
                </Col>
                <Col lg={8} xs={24}>
                    <Form.Item>
                        <br />
                        <Button htmlType="submit" type="primary">
                            Filter
                        </Button>
                    </Form.Item>
                </Col>
            </Row>
        </Form>
    );
};
